import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { ConfigService } from './config.service';

export interface PortalApp {
  id: string;
  name: string;
  description: string;
  url: string;
  clientId: string;
  requiredRoles: string[];
  icon: string;
  color: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppService {

  constructor(
    private keycloak: KeycloakService,
    private configService: ConfigService
  ) { }

  /**
   * Récupère la liste des clientId présents dans le champ 'aud' du token,
   * après filtrage des audiences exclues.
   * @returns Promise<string[]> - Tableau des clientId accessibles
   */
  async getValidAudiences(): Promise<string[]> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        return [];
      }

      // Token valide
      try {
        await this.keycloak.updateToken(5);
      } catch (tokenError) {
        console.warn('[AppService] Impossible de rafraîchir le token', tokenError);
      }

      const keycloakInstance = this.keycloak.getKeycloakInstance();
      const tokenParsed = keycloakInstance.tokenParsed;
      if (!tokenParsed?.aud) {
        return [];
      }

      const aud = tokenParsed.aud;
      const audiences: string[] = Array.isArray(aud) ? aud : [aud];

      const excluded = this.configService.getExcludedAudiences();
      return audiences.filter(a => !excluded.includes(a));
    } catch (error) {
      console.error('[AppService] Erreur récupération audiences', error);
      return [];
    }
  }

  /**
   * Récupère les applications accessibles par l'utilisateur.
   * L'accès est déterminé par la présence du clientId dans le champ 'aud' du token.
   */
  async getAccessibleApps(): Promise<PortalApp[]> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        console.log('[AppService] Utilisateur non connecté');
        return [];
      }

      // S'assurer que le token est valide et parsé
      try {
        await this.keycloak.updateToken(5);
      } catch (tokenError) {
        console.warn('[AppService] Impossible de rafraîchir le token, tentative avec token existant', tokenError);
      }

      const keycloakInstance = this.keycloak.getKeycloakInstance();
      const tokenParsed = keycloakInstance.tokenParsed;
      if (!tokenParsed) {
        console.warn('[AppService] Token non disponible ou non parsé');
        return [];
      }

      // Récupération du champ 'aud' (string ou tableau)
      const aud = tokenParsed.aud;
      let audiences: string[] = [];
      if (Array.isArray(aud)) {
        audiences = aud;
      } else if (typeof aud === 'string') {
        audiences = [aud];
      } else {
        console.warn('[AppService] Champ aud manquant ou invalide');
        return [];
      }

      // Filtrage des audiences exclues
      const excluded = this.configService.getExcludedAudiences();
      const validAudiences = audiences.filter(a => !excluded.includes(a));

      console.log('[AppService] Audiences valides:', validAudiences);

      // Transformation en objets PortalApp
      const apps: PortalApp[] = [];
      for (const clientId of validAudiences) {
        const app = this.configService.getAppByClientId(clientId);
        if (app) {
          apps.push(app);
        } else {
          console.warn(`[AppService] Aucune configuration pour le clientId: ${clientId}`);
        }
      }

      console.log('[AppService] Applications accessibles:', apps.length);
      return apps;
    } catch (error) {
      console.error('[AppService] Erreur lors de la récupération des applications:', error);
      return [];
    }
  }

  /**
   * Récupère tous les rôles de l'utilisateur (inchangé).
   */
  async getUserRoles(): Promise<string[]> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) return [];

      const keycloakInstance = this.keycloak.getKeycloakInstance();
      const realmRoles = keycloakInstance.tokenParsed?.realm_access?.roles || [];
      const resourceAccess = keycloakInstance.tokenParsed?.resource_access || {};
      const clientRoles: string[] = [];

      Object.keys(resourceAccess).forEach(clientId => {
        const roles = resourceAccess[clientId]?.roles || [];
        clientRoles.push(...roles);
      });

      return [...new Set([...realmRoles, ...clientRoles])];
    } catch (error) {
      console.error('[AppService] Erreur lors de la récupération des rôles:', error);
      return [];
    }
  }

  /**
   * Vérifie si l'utilisateur a accès à une application spécifique.
   */
  async hasAccessToApp(appIdOrClientId: string): Promise<boolean> {
    try {
      const apps = await this.getAccessibleApps();
      return apps.some(a => a.id === appIdOrClientId || a.clientId === appIdOrClientId);
    } catch (error) {
      console.error('[AppService] Erreur lors de la vérification d\'accès:', error);
      return false;
    }
  }

  /**
   * Retourne toutes les applications connues (sans filtrage).
   */
  getAllApps(): PortalApp[] {
    return this.configService.getAllApps();
  }

  /**
   * Retourne une application par son ID ou clientId.
   */
  getAppById(appId: string): PortalApp | undefined {
    let app = this.configService.getAppByClientId(appId);
    if (app) return app;
    return this.getAllApps().find(a => a.id === appId);
  }
}